import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { toast } from 'sonner';
import Page from './Page';
import SpreadEditor, { type SpreadEditorRef, type ApprovalData } from './SpreadEditor';
import TopEditor from './TopEditor';
import type { PageData, PageType } from '../types/editing';
import { useWordDataContext } from '../contexts/WordDataContext';
import { 
  markTtsIncludedElements, 
  processWordTimings, 
  startWordHighlighting, 
  cleanupWordHighlighting,
  ENABLE_WORD_HIGHLIGHTING
} from '../utils/wordHighlighting';


interface BookReaderProps {
  isbn?: string;
  onClose?: () => void;
}

const BookReader: React.FC<BookReaderProps> = ({ isbn: propIsbn, onClose }) => {
  const { isbn: urlIsbn } = useParams<{ isbn: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: wordDataState, actions: wordDataActions } = useWordDataContext();
  
  // Use URL parameter if available, otherwise use prop (for backward compatibility)
  const isbn = urlIsbn || propIsbn;
  
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // Preserve books-to-edit parameter when navigating back
      const booksToEdit = searchParams.get('books-to-edit');
      const url = booksToEdit ? `/?books-to-edit=${encodeURIComponent(booksToEdit)}` : '/';
      navigate(url);
    }
  };
  const [pages, setPages] = useState<PageData[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Get initial page from URL parameter
  const urlPage = searchParams.get('page');
  const initialPage = urlPage ? parseInt(urlPage, 10) : 1;
  
  // Initialize page state from URL parameter
  const [currentRightPage, setCurrentRightPage] = useState(initialPage);
  const [currentLeftPage, setCurrentLeftPage] = useState(initialPage % 2 === 1 ? Math.max(initialPage - 1, 0) : Math.max(initialPage - 2, 0));
  const [showOverlays, setShowOverlays] = useState(true);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const [spineData, setSpineData] = useState<any>(null);
  const lastUrlPageRef = useRef<number | null>(null);
  const isUpdatingUrlRef = useRef(false);
  const lastProcessedInitialPageRef = useRef<number | null>(null);
  const currentRightPageRef = useRef(currentRightPage);

  // Add state for allPageElements
  const [allPageElements, setAllPageElements] = useState<React.ReactElement[]>([]);
  
  // TTS state
  const [ttsActive, setTtsActive] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPaused, setTtsPaused] = useState(false);
  // Voice settings are now retrieved from SpreadEditor via spreadEditorRef
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [currentWebSocket, setCurrentWebSocket] = useState<WebSocket | null>(null);
  const [bookMetadata, setBookMetadata] = useState<any>(null);
  const [approval, setApproval] = useState<any>(null);
  const spreadEditorRef = useRef<SpreadEditorRef>(null);
  const highlightCleanupRef = useRef<(() => void) | null>(null);
  const lastIsbnRef = useRef<string | undefined>(undefined);
  const lastEscapePressRef = useRef<number | null>(null);
  const [topEditorState, setTopEditorState] = useState<{
    hasChanges: boolean;
    isSaving: boolean;
    saveButtonText: string;
    saveButtonTitle: string;
    currentApproval?: ApprovalData;
  }>({
    hasChanges: false,
    isSaving: false,
    saveButtonText: 'No Changes',
    saveButtonTitle: 'No changes to save',
    currentApproval: undefined,
  });
  const [toggleState, setToggleState] = useState({
    showOverlays: true,
    showBlockBg: true,
    invertPage: false,
  });
  const [isBlockEditing, setIsBlockEditing] = useState(false);

  // Track block editing state from SpreadEditor
  useEffect(() => {
    const checkBlockEditing = () => {
      const blockEditing = spreadEditorRef.current?.isBlockEditing() || false;
      setIsBlockEditing(prev => {
        if (prev !== blockEditing) {
          return blockEditing;
        }
        return prev;
      });
    };
    
    // Check immediately
    checkBlockEditing();
    
    // Check periodically to catch changes (block editing state changes don't trigger re-renders in parent)
    // Using a reasonable interval to avoid performance issues
    const interval = setInterval(checkBlockEditing, 200);
    
    return () => clearInterval(interval);
  }, [isbn]); // Re-run when ISBN changes (new book, new SpreadEditor instance)

  // Reset WordDataContext state when ISBN changes (switching books)
  useEffect(() => {
    if (isbn && isbn !== lastIsbnRef.current) {
      // ISBN has changed, clear all word data from previous book
      wordDataActions.resetState();
      lastIsbnRef.current = isbn;
    }
  }, [isbn, wordDataActions]);

  useEffect(() => {
    const loadBook = async () => {
      try {
        const startTime = Date.now();
        
        // Import FolderService
        const { FolderService } = await import('../services/FolderService');
        const folder = FolderService.getFolder();
        
        if (!folder) {
          console.error('No folder selected, cannot load book');
          return;
        }
        
        // First try to load spine.json from backend API (this has the real filenames)
        const { authenticatedFetch } = await import('../utils/api');
        const spineUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
        const response = await authenticatedFetch(spineUrl);
        const loadTime = Date.now() - startTime;
        
        if (response.ok) {
          const parseStartTime = Date.now();
          const spineData = await response.json();
          const parseTime = Date.now() - parseStartTime;
          
          
          // Use all pages (including blocked files for editing)
          const allPages = spineData.pages;
          
          // DEBUG: Log spine data structure
          
          setPages(allPages);
          setTotalPages(allPages.length);
          setSpineData(spineData); // Store spine data for approval data
          setApproval(spineData.approval || null); // Load approval data
          
          // Preload images using backend API with real filenames (improved based on JS implementation)
          const { preloadAuthenticatedImage } = await import('../utils/imageLoader');
          const imagePromises = spineData.pages.map(async (page: PageData) => {
            try {
              const imageUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/images/${page.filename}`);
              await preloadAuthenticatedImage(imageUrl);
            } catch (error) {
              // Still resolve to not block the loading
              console.error(`Failed to preload image ${page.filename}:`, error);
            }
          });
          await Promise.all(imagePromises);
          return;
        }
        
        // If spine failed to load, show error - don't guess filenames
        const errorText = await response.text();
        let errorMessage = 'Failed to load book spine';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      } catch (error) {
        // Log error to console so user can see what went wrong
        console.error('[BookReader] Failed to load book:', error);
        setPages([]);
        setTotalPages(0);
      }
    };
    if (isbn) {
      loadBook();
    }
  }, [isbn]);

  // Handle pageType change
  const handlePageTypeChange = useCallback((pageIndex: number, pageType: PageType) => {
    setPages(prevPages => {
      const updatedPages = prevPages.map(page => 
        page['page-index'] === pageIndex 
          ? { ...page, pageType }
          : page
      );
      
      // Also update spineData if it exists
      if (spineData) {
        setSpineData((prevSpine: any) => {
          if (prevSpine?.pages) {
            const updatedSpinePages = prevSpine.pages.map((page: PageData) =>
              page['page-index'] === pageIndex
                ? { ...page, pageType }
                : page
            );
            return { ...prevSpine, pages: updatedSpinePages };
          }
          return prevSpine;
        });
      }
      
      return updatedPages;
    });
  }, [spineData]);

  // Update page in URL
  const updatePageInUrl = useCallback((page: number) => {
    if (lastUrlPageRef.current === page) return; // Already set
    isUpdatingUrlRef.current = true;
    lastUrlPageRef.current = page;
    lastProcessedInitialPageRef.current = page; // Mark this page as processed
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
    // Reset flag after a short delay
    setTimeout(() => {
      isUpdatingUrlRef.current = false;
    }, 100);
  }, [setSearchParams]);

  // Keep ref in sync with state
  useEffect(() => {
    currentRightPageRef.current = currentRightPage;
  }, [currentRightPage]);

  // Initialize page from URL when pages load or URL changes externally (e.g., browser refresh)
  useEffect(() => {
    // Skip if we're updating the URL ourselves
    if (isUpdatingUrlRef.current) return;
    
    // Get current page from URL
    const currentUrlPage = searchParams.get('page');
    const urlPageNum = currentUrlPage ? parseInt(currentUrlPage, 10) : 1;
    
    // Skip if we've already processed this URL page
    if (lastProcessedInitialPageRef.current === urlPageNum && pages.length > 0) return;
    
    if (pages.length > 0 && urlPageNum >= 1) {
      // Validate initial page is within bounds
      const validPage = Math.min(Math.max(urlPageNum, 1), pages.length);
      const rightPage = validPage;
      const leftPage = validPage % 2 === 1 ? validPage - 1 : validPage - 2;
      
      // Update if different from current state (handles browser refresh)
      // Use ref to get current value without causing dependency loop
      if (rightPage !== currentRightPageRef.current) {
        lastUrlPageRef.current = rightPage; // Track what we're setting
        lastProcessedInitialPageRef.current = urlPageNum; // Track what we've processed
        setCurrentRightPage(rightPage);
        setCurrentLeftPage(Math.max(leftPage, 0));
      } else {
        // Even if same, mark as processed to prevent re-running
        lastProcessedInitialPageRef.current = urlPageNum;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length, searchParams]); // Removed currentRightPage to prevent loop

  // Update URL when page changes (but not on initial load from URL)
  useEffect(() => {
    if (pages.length > 0 && currentRightPage >= 1) {
      const currentUrlPage = searchParams.get('page');
      const currentUrlPageNum = currentUrlPage ? parseInt(currentUrlPage, 10) : 1;
      
      // Only update URL if it's different from what user is currently viewing
      // and we're not already updating it
      if (currentRightPage !== currentUrlPageNum && !isUpdatingUrlRef.current && lastUrlPageRef.current !== currentRightPage) {
        updatePageInUrl(currentRightPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRightPage, pages.length, updatePageInUrl]); // Removed searchParams to prevent loop

  useEffect(() => {
    if (pages.length > 0) {
      updatePositionClasses();
    }
  }, [pages, isbn, currentRightPage, currentLeftPage, showOverlays]); // Added showOverlays so Page components get updated prop

  // Debug function to check which pages have isInSpread=true
  useEffect(() => {
    (window as any).checkPagesInSpread = () => {
      const instances = (window as any).__pageEditorInstances || new Map();
      const results: any[] = [];
      instances.forEach((data: any, pageIndex: number) => {
        if (data.isInSpread) {
          results.push({
            pageIndex,
            currentLeftPage: data.currentLeftPage,
            currentRightPage: data.currentRightPage,
            showOverlays: data.showOverlays,
            localBlocksCount: data.localBlocksCount
          });
        }
      });
      console.log('Pages with isInSpread=true:', results);
      console.log('Current spread should be:', { left: currentLeftPage, right: currentRightPage });
      return results;
    };
    console.log('[BookReader] Debug function available: window.checkPagesInSpread()');
  }, [currentLeftPage, currentRightPage]);

  // Debug function to inspect word position, size, padding, and border
  useEffect(() => {
    (window as any).inspectWord = function(pageSideOrIndex: string | number, blockIndex: number, wordIndex: number) {
      // Find the page container
      let pageContainer: HTMLElement | null = null;
      
      if (typeof pageSideOrIndex === 'string') {
        // Find current left/right page
        const pages = document.querySelectorAll('.page[data-page-index]');
        let targetPage: Element | null = null;
        
        if (pageSideOrIndex === 'right') {
          // Right pages are odd indices (1, 3, 5...)
          // Find the current right page (has class 'current-right-page')
          targetPage = Array.from(pages).find(page => 
            page.classList.contains('current-right-page')
          ) || null;
        } else if (pageSideOrIndex === 'left') {
          // Left pages are even indices (0, 2, 4...)
          // Find the current left page (has class 'current-left-page')
          targetPage = Array.from(pages).find(page => 
            page.classList.contains('current-left-page')
          ) || null;
        }
        
        if (!targetPage) {
          return { error: `Could not find ${pageSideOrIndex} page` };
        }
        
        const pageIndex = parseInt(targetPage.getAttribute('data-page-index') || '0');
        pageContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageIndex}"]`);
      } else {
        // Direct page index
        pageContainer = document.querySelector(`.word-overlay-container[data-page-overlay="page-${pageSideOrIndex}"]`);
      }
      
      if (!pageContainer) {
        return { error: 'Could not find page container' };
      }
      
      // Find the block (blocks are 0-indexed in DOM, but user might use 1-indexed)
      const blocks = pageContainer.querySelectorAll('.overlay-block');
      const blockIndexZeroBased = blockIndex - 1; // Convert to 0-based
      
      if (blockIndexZeroBased < 0 || blockIndexZeroBased >= blocks.length) {
        return { 
          error: `Block index ${blockIndex} out of range. Found ${blocks.length} blocks.`,
          availableBlocks: blocks.length 
        };
      }
      
      const block = blocks[blockIndexZeroBased];
      const blockId = block.getAttribute('data-block-id');
      
      // Find words - they might be direct children of block OR in a portal (resize/move mode)
      // First try to find words as direct children of the block
      let words = block.querySelectorAll('.overlay-word');
      
      // If no words found (might be in portal), find all words in container and filter by position
      if (words.length === 0) {
        const blockRect = block.getBoundingClientRect();
        const allWords = pageContainer.querySelectorAll('.overlay-word');
        const wordsInBlock: Element[] = [];
        
        allWords.forEach(wordEl => {
          const wordRect = wordEl.getBoundingClientRect();
          // Check if word is positioned within or near the block's bounds
          // Allow some tolerance for words that might be slightly outside due to positioning
          const tolerance = 50; // pixels
          if (wordRect.left >= blockRect.left - tolerance &&
              wordRect.top >= blockRect.top - tolerance &&
              wordRect.right <= blockRect.right + tolerance &&
              wordRect.bottom <= blockRect.bottom + tolerance) {
            wordsInBlock.push(wordEl);
          }
        });
        
        words = wordsInBlock as any;
      }
      
      const wordIndexZeroBased = wordIndex - 1; // Convert to 0-based
      
      if (wordIndexZeroBased < 0 || wordIndexZeroBased >= words.length) {
        return { 
          error: `Word index ${wordIndex} out of range. Found ${words.length} words in block ${blockIndex}.`,
          availableWords: words.length,
          blockId: blockId,
          note: words.length === 0 ? 'No words found - they might be in portal (resize/move mode)' : undefined
        };
      }
      
      const word = words[wordIndexZeroBased] as HTMLElement;
      const wordId = word.getAttribute('data-word-id');
      
      // Get computed styles
      const styles = window.getComputedStyle(word);
      const rect = word.getBoundingClientRect();
      const containerRect = pageContainer.getBoundingClientRect();
      
      // Calculate position relative to container
      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;
      
      // Extract padding and border values
      const padding = {
        top: parseFloat(styles.paddingTop) || 0,
        right: parseFloat(styles.paddingRight) || 0,
        bottom: parseFloat(styles.paddingBottom) || 0,
        left: parseFloat(styles.paddingLeft) || 0
      };
      
      const border = {
        top: parseFloat(styles.borderTopWidth) || 0,
        right: parseFloat(styles.borderRightWidth) || 0,
        bottom: parseFloat(styles.borderBottomWidth) || 0,
        left: parseFloat(styles.borderLeftWidth) || 0
      };
      
      const margin = {
        top: parseFloat(styles.marginTop) || 0,
        right: parseFloat(styles.marginRight) || 0,
        bottom: parseFloat(styles.marginBottom) || 0,
        left: parseFloat(styles.marginLeft) || 0
      };
      
      const result = {
        page: {
          side: typeof pageSideOrIndex === 'string' ? pageSideOrIndex : (pageSideOrIndex % 2 === 1 ? 'right' : 'left'),
          index: typeof pageSideOrIndex === 'string' 
            ? parseInt(pageContainer.getAttribute('data-page-overlay')?.replace('page-', '') || '0')
            : pageSideOrIndex
        },
        block: {
          index: blockIndex,
          id: blockId,
          totalWords: words.length
        },
        word: {
          index: wordIndex,
          id: wordId,
          text: word.textContent?.trim() || ''
        },
        position: {
          absolute: {
            x: rect.left,
            y: rect.top
          },
          relativeToContainer: {
            x: relativeX,
            y: relativeY
          },
          cssLeft: styles.left,
          cssTop: styles.top
        },
        size: {
          width: rect.width,
          height: rect.height,
          cssWidth: styles.width,
          cssHeight: styles.height
        },
        padding: padding,
        border: {
          top: border.top,
          right: border.right,
          bottom: border.bottom,
          left: border.left,
          style: {
            top: styles.borderTopStyle,
            right: styles.borderRightStyle,
            bottom: styles.borderBottomStyle,
            left: styles.borderLeftStyle
          },
          color: {
            top: styles.borderTopColor,
            right: styles.borderRightColor,
            bottom: styles.borderBottomColor,
            left: styles.borderLeftColor
          }
        },
        margin: margin,
        boxSizing: styles.boxSizing,
        element: word
      };
      
      // Output the object
      console.log(result);
      
      // Output plain text summary with just the numbers
      console.log('\n--- NUMBERS ---');
      console.log(`Position (relative to container): x=${relativeX.toFixed(2)}px, y=${relativeY.toFixed(2)}px`);
      console.log(`Position (absolute): x=${rect.left.toFixed(2)}px, y=${rect.top.toFixed(2)}px`);
      console.log(`Size: width=${rect.width.toFixed(2)}px, height=${rect.height.toFixed(2)}px`);
      console.log(`Padding: top=${padding.top}px, right=${padding.right}px, bottom=${padding.bottom}px, left=${padding.left}px`);
      console.log(`Border: top=${border.top}px, right=${border.right}px, bottom=${border.bottom}px, left=${border.left}px`);
      console.log(`Margin: top=${margin.top}px, right=${margin.right}px, bottom=${margin.bottom}px, left=${margin.left}px`);
      console.log(`Box-sizing: ${styles.boxSizing}`);
      
      return result;
    };
    console.log('[BookReader] Debug function available: window.inspectWord(pageSideOrIndex, blockIndex, wordIndex)');
    console.log('  Examples: inspectWord("right", 1, 1) or inspectWord("left", 2, 3) or inspectWord(24, 1, 1)');
  }, []);

  // Update activeSpread in WordDataContext when spread changes
  useEffect(() => {
    wordDataActions.setActiveSpread(
      currentLeftPage > 0 ? currentLeftPage : null,
      currentRightPage > 0 ? currentRightPage : null
    );
  }, [currentLeftPage, currentRightPage, wordDataActions]);

  // Initialize showWords to false - no localStorage persistence for now to avoid infinite loops
  useEffect(() => {
    wordDataActions.setShowWords(false);
  }, []); // Only run once on mount

  // Cleanup TTS resources on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      if (currentWebSocket && currentWebSocket.readyState === WebSocket.OPEN) {
        currentWebSocket.close();
      }
    };
  }, [currentAudio, currentWebSocket]);

  // Helper function to get all words in read order for both current pages
  const getReadOrderText = async (): Promise<string> => {
    if (!isbn || pages.length === 0) {
      return '';
    }

    // Helper function to process a page
    const processPage = async (pageFilename: string, pageName: string): Promise<{ text: string; blocks: any[] }> => {
      try {
        const { FolderService } = await import('../services/FolderService');
        const { authenticatedFetch } = await import('../utils/api');
        const pageUrl = FolderService.getPageJsonUrl(isbn, pageFilename);
        const response = await authenticatedFetch(pageUrl);
        if (response.ok) {
          const wordData = await response.json();
          if (wordData.blocks && Array.isArray(wordData.blocks)) {
            const readBlocks = wordData.blocks
              .filter((block: any) => block.readBlock === true)
              .sort((a: any, b: any) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
            
              const text = readBlocks
                .map((block: any, index: number) => {
                  const wordsText = block.words?.map((word: any) => word.text).join(' ') || '';
                  // Add .  if blockType changes from previous block
                  if (index > 0 && readBlocks[index - 1].blockType !== block.blockType) {
                    return '. ' + wordsText;
                  }
                  return wordsText;
                })
                .join(' ')
                .trim();
            
            if (text) {
              console.log(`Found ${readBlocks.length} read blocks for ${pageName}`);
            }
            
            return { text, blocks: readBlocks };
          }
        }
      } catch (error) {
        console.error(`Error loading ${pageName} word data:`, error);
      }
      return { text: '', blocks: [] };
    };

    try {
      // Get both current pages
      const leftPage = pages.find(page => page['page-index'] === currentLeftPage);
      const rightPage = pages.find(page => page['page-index'] === currentRightPage);
      
      // Process both pages - only if text_extracted is "enhanced"
      const [leftResult, rightResult] = await Promise.all([
        (leftPage && leftPage.words && (leftPage.text_extracted === 'enhanced' || leftPage.text_extracted === true)) 
          ? processPage(leftPage.words, `left page ${currentLeftPage}`) 
          : Promise.resolve({ text: '', blocks: [] }),
        (rightPage && rightPage.words && (rightPage.text_extracted === 'enhanced' || rightPage.text_extracted === true)) 
          ? processPage(rightPage.words, `right page ${currentRightPage}`) 
          : Promise.resolve({ text: '', blocks: [] }),
      ]);
      
      // Combine texts, adding <break /> between pages if block types differ
      const allTexts = [leftResult.text, rightResult.text].filter(t => t);
      
      if (allTexts.length === 0) {
        return '';
      }
      
      let combinedText = '';
      if (allTexts.length === 2 && leftResult.blocks.length > 0 && rightResult.blocks.length > 0) {
        const lastLeftBlockType = leftResult.blocks[leftResult.blocks.length - 1].blockType;
        const firstRightBlockType = rightResult.blocks[0].blockType;
        
        // Add . if block types differ between pages
        combinedText = lastLeftBlockType !== firstRightBlockType 
          ? allTexts.join(' . ').trim()
          : allTexts.join(' ').trim();
      } else {
        combinedText = allTexts.join(' ').trim();
      }
      
      console.log(`Total text length: ${combinedText.length} characters`);
      
      return combinedText;
    } catch (error) {
      console.error('Error loading word data for TTS:', error);
      return '';
    }
  };

  // Recursive function to continue reading through the book
  const continueReadingBook = async (rightPage?: number | null, leftPage?: number | null) => {
    // Use passed page numbers or current state
    // If rightPage is explicitly null (last left-only page), use null; if undefined, use current state
    const currentRight = rightPage !== undefined ? rightPage : currentRightPage;
    const currentLeft = leftPage !== undefined ? leftPage : currentLeftPage;
    
    console.log(`continueReadingBook: currentRightPage=${currentRight}, currentLeftPage=${currentLeft}, totalPages=${totalPages}, ttsActive=${ttsActive}, ttsLoading=${ttsLoading}`);
    
    // Check if we're past the end of the book (pages are 1-indexed, totalPages is count)
    // Allow currentRight to be null (for last left-only page) or check if it exceeds totalPages
    if (currentRight !== null && currentRight > totalPages) {
      console.log('Reached end of book');
      setTtsActive(false);
      setTtsLoading(false);
      setTtsPaused(false);
      setCurrentAudio(null);
      if (currentWebSocket) {
        currentWebSocket.close();
        setCurrentWebSocket(null);
      }
      return;
    }
    
    // If we're on the last left-only page, check if we're already playing it
    if (currentRight === null && currentLeft === totalPages) {
      // If we're already playing audio for this page, don't process it again
      if (ttsActive || ttsLoading) {
        console.log('Already playing last left-only page, skipping duplicate processing');
        return;
      }
      // Otherwise, continue to process this last page
    }

    // Wait for page turn animation if animating
    if (isAnimating) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // Fetch the current pages based on the page numbers
    const readOrderResult = await (async () => {
      const empty = { leftResult: { text: '', blocks: [] as any[] }, rightResult: { text: '', blocks: [] as any[] }, pageQueue: [] as { pageIndex: number; text: string }[] };
      if (!isbn || pages.length === 0) {
        return empty;
      }

      try {
        // Get both current pages (rightPageData can be null if we're on last left-only page)
        const leftPageData = pages.find(page => page['page-index'] === currentLeft);
        const rightPageData = currentRight !== null ? pages.find(page => page['page-index'] === currentRight) : null;
        
        console.log(`Fetching text for left=${currentLeft}, right=${currentRight}`);
        
        let leftReadBlocks: any[] = [];
        let rightReadBlocks: any[] = [];
        let leftText = '';
        let rightText = '';
        
        // Helper function to process a page
        const processPage = async (pageFilename: string, pageName: string): Promise<{ text: string; blocks: any[] }> => {
          try {
            const { FolderService } = await import('../services/FolderService');
            const { authenticatedFetch } = await import('../utils/api');
            const pageUrl = FolderService.getPageJsonUrl(isbn, pageFilename);
            const response = await authenticatedFetch(pageUrl);
            if (response.ok) {
              const wordData = await response.json();
              if (wordData.blocks && Array.isArray(wordData.blocks)) {
                // Debug: Log all block readBlock values
                console.log(`${pageName} - All blocks:`);
                wordData.blocks.forEach((b: any, i: number) => {
                  const wordsText = b.words?.map((word: any) => word.text).join(' ') || '';
                  console.log(`  Block ${i}: id=${b.blockId}, readBlock=${b.readBlock}, words="${wordsText}"`);
                });
                
                const readBlocks = wordData.blocks
                  .filter((block: any) => block.readBlock === true)
                  .sort((a: any, b: any) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
                
                console.log(`${pageName} - Filtered readBlocks count: ${readBlocks.length} out of ${wordData.blocks.length} total blocks`);
                
                const text = readBlocks
                  .map((block: any, index: number) => {
                    const wordsText = block.words?.map((word: any) => word.text).join(' ') || '';
                    // Add .  if blockType changes from previous block
                    if (index > 0 && readBlocks[index - 1].blockType !== block.blockType) {
                      return '. ' + wordsText;
                    }
                    return wordsText;
                  })
                  .join(' ')
                  .trim();
                
                console.log(`Found ${readBlocks.length} read blocks for ${pageName}, text length=${text.length}`);
                
                return { text, blocks: readBlocks };
              }
            }
          } catch (error) {
            console.error(`Error loading ${pageName} word data:`, error);
          }
          return { text: '', blocks: [] };
        };

        // Process both pages - only if text_extracted is "enhanced"
        const [leftResult, rightResult] = await Promise.all([
          (leftPageData && leftPageData.words && (leftPageData.text_extracted === 'enhanced' || leftPageData.text_extracted === true)) 
            ? processPage(leftPageData.words, `left page ${currentLeft}`) 
            : Promise.resolve({ text: '', blocks: [] }),
          (rightPageData && rightPageData.words && (rightPageData.text_extracted === 'enhanced' || rightPageData.text_extracted === true)) 
            ? processPage(rightPageData.words, `right page ${currentRight}`) 
            : Promise.resolve({ text: '', blocks: [] }),
        ]);
        
        // Mark blocks and words that are included in TTS (only if highlighting is enabled)
        if (ENABLE_WORD_HIGHLIGHTING) {
          const pageIndicesForMarking: number[] = [];
          if (leftResult.blocks.length > 0) pageIndicesForMarking.push(currentLeft);
          if (rightResult.blocks.length > 0 && currentRight !== null) pageIndicesForMarking.push(currentRight);
          
          if (pageIndicesForMarking.length > 0) {
            // Create a temporary blocksByPage map for marking
            const tempBlocksByPage = new Map<number, any[]>();
            if (leftResult.blocks.length > 0) {
              // Get full blocks from wordDataState for left page
              const leftBlocks = wordDataState.blocksByPage.get(currentLeft) || [];
              tempBlocksByPage.set(currentLeft, leftBlocks);
            }
            if (rightResult.blocks.length > 0 && currentRight !== null) {
              // Get full blocks from wordDataState for right page
              const rightBlocks = wordDataState.blocksByPage.get(currentRight) || [];
              tempBlocksByPage.set(currentRight, rightBlocks);
            }
            
            // Mark elements after a short delay to ensure DOM is ready
            setTimeout(() => {
              markTtsIncludedElements(pageIndicesForMarking, tempBlocksByPage);
            }, 100);
          }
        }
        
        // Build queue: one entry per page with text (send 1 page at a time) (send 1 page at a time)
        const pageQueue: { pageIndex: number; text: string }[] = [];
        if (leftResult.text.trim()) pageQueue.push({ pageIndex: currentLeft, text: leftResult.text });
        if (currentRight !== null && rightResult.text.trim()) pageQueue.push({ pageIndex: currentRight, text: rightResult.text });
        return { leftResult, rightResult, pageQueue };
      } catch (error) {
        console.error('Error loading word data for TTS:', error);
        return empty;
      }
    })();
    
    const { leftResult, rightResult, pageQueue } = readOrderResult as { leftResult: { text: string; blocks: any[] }; rightResult: { text: string; blocks: any[] }; pageQueue: { pageIndex: number; text: string }[] };
    
    if (pageQueue.length === 0) {
      // No read blocks on this page, skip to next
      console.log(`No read blocks on page ${currentRight}, skipping...`);
      
      // Check if we're already at or past the last page (pages are 1-indexed, totalPages is count)
      // Handle both regular pages and last left-only page
      if (currentRight === null && currentLeft === totalPages) {
        // Already on last left-only page with no read blocks
        console.log('Reached end of book (no read blocks on last left-only page)');
        setTtsActive(false);
        setTtsLoading(false);
        setTtsPaused(false);
        setCurrentAudio(null);
        if (currentWebSocket) {
          currentWebSocket.close();
          setCurrentWebSocket(null);
        }
        return;
      } else if (currentRight !== null && currentRight >= totalPages) {
        console.log('Reached end of book (no read blocks on last page)');
        setTtsActive(false);
        setTtsLoading(false);
        setTtsPaused(false);
        setCurrentAudio(null);
        if (currentWebSocket) {
          currentWebSocket.close();
          setCurrentWebSocket(null);
        }
        return;
      }
      
      // Calculate next page numbers
      let nextRight: number | null;
      let nextLeft: number | null;
      
      // Check if next spread would be the last spread (left-only page)
      if (currentRight !== null && currentRight + 2 > totalPages) {
        // Check if there's a last left-only page
        if (currentRight + 1 === totalPages) {
          // Next spread is the last left-only page
          nextRight = null;
          nextLeft = totalPages;
        } else {
          // Already past the end
          console.log('Reached end of book (no read blocks, next page would exceed)');
          setTtsActive(false);
          setTtsLoading(false);
          setTtsPaused(false);
          setCurrentAudio(null);
          if (currentWebSocket) {
            currentWebSocket.close();
            setCurrentWebSocket(null);
          }
          return;
        }
      } else if (currentRight === null) {
        // Already on last left-only page, can't advance further
        console.log('Reached end of book (already on last left-only page)');
        setTtsActive(false);
        setTtsLoading(false);
        setTtsPaused(false);
        setCurrentAudio(null);
        if (currentWebSocket) {
          currentWebSocket.close();
          setCurrentWebSocket(null);
        }
        return;
      } else {
        // Normal case: full spread ahead
        nextRight = currentRight + 2;
        nextLeft = currentLeft === 0 ? 2 : currentLeft + 2;
      }
      
      setIsAnimating(true);
      setCurrentRightPage(nextRight);
      setCurrentLeftPage(nextLeft);
      setTimeout(() => setIsAnimating(false), 150);
      
      // Continue to next page after animation
      // Pass null explicitly for last left-only page
      setTimeout(() => continueReadingBook(nextRight, nextLeft), 300);
      return;
    }

    // Validate voice selection for ElevenLabs
    const audioService = spreadEditorRef.current?.getAudioService() || 'azure';
    const voiceName = spreadEditorRef.current?.getSelectedVoice() || '';
    
    if (audioService === 'elevenlabs' && (!voiceName || voiceName.trim() === '' || voiceName === 'Select voice...')) {
      // Reset TTS state since play never happened (same as pressing stop)
      setTtsActive(false);
      setTtsLoading(false);
      setTtsPaused(false);
      toast.error('Please select a voice before playing', {
        duration: 3000,
      });
      return;
    }

    setTtsLoading(true);

    const finalVoiceName = voiceName || 'default';
    const elevenlabsModel = spreadEditorRef.current?.getElevenlabsModel() || 'default';
    const wsEndpoint = 'wss://audio.getcurioso.com/ws';

    const advanceAndContinue = () => {
      let nextRight: number | null;
      let nextLeft: number | null;
      if (currentRight !== null && currentRight + 2 > totalPages) {
        if (currentRight + 1 === totalPages) {
          nextRight = null;
          nextLeft = totalPages;
        } else {
          setTtsActive(false);
          setTtsLoading(false);
          setTtsPaused(false);
          setCurrentAudio(null);
          if (currentWebSocket) {
            currentWebSocket.close();
            setCurrentWebSocket(null);
          }
          return;
        }
      } else if (currentRight === null) {
        setTtsActive(false);
        setTtsLoading(false);
        setTtsPaused(false);
        setCurrentAudio(null);
        if (currentWebSocket) {
          currentWebSocket.close();
          setCurrentWebSocket(null);
        }
        return;
      } else {
        nextRight = currentRight + 2;
        nextLeft = currentLeft === 0 ? 2 : currentLeft + 2;
      }
      setIsAnimating(true);
      setCurrentRightPage(nextRight);
      setCurrentLeftPage(nextLeft);
      setTimeout(() => setIsAnimating(false), 150);
      setTimeout(() => continueReadingBook(nextRight, nextLeft), 300);
    };

    try {
      for (const entry of pageQueue) {
        const { pageIndex, text } = entry;
        await new Promise<void>((resolve, reject) => {
          const ws = new WebSocket(wsEndpoint);
          setCurrentWebSocket(ws);
          ws.onopen = () => {
            const md5Id = CryptoJS.MD5(text).toString();
            const message: any = {
              data: text,
              area: 'book',
              areaItemId: isbn,
              itemSubId: String(pageIndex - 1),
              audioService: audioService,
              voiceName: finalVoiceName,
              md5Id: md5Id
            };
            if (audioService === 'elevenlabs' && elevenlabsModel) message.model = elevenlabsModel;
            ws.send(JSON.stringify(message));
          };
          ws.onmessage = (event: MessageEvent) => {
            try {
              const response = JSON.parse(event.data);
              if (response.audioAvailable && response.audioUrl) {
                if (ENABLE_WORD_HIGHLIGHTING && response.wordTimings && Array.isArray(response.wordTimings) && response.wordTimings.length > 0) {
                  setTimeout(() => {
                    processWordTimings(response.wordTimings, [pageIndex]);
                  }, 200);
                }
                const audio = new Audio(response.audioUrl);
                setCurrentAudio(audio);
                audio.onloadeddata = () => {
                  setTtsLoading(false);
                  if (ENABLE_WORD_HIGHLIGHTING && response.wordTimings && Array.isArray(response.wordTimings) && response.wordTimings.length > 0) {
                    if (highlightCleanupRef.current) {
                      highlightCleanupRef.current();
                      highlightCleanupRef.current = null;
                    }
                    const cleanup = startWordHighlighting(audio, response.wordTimings);
                    highlightCleanupRef.current = cleanup;
                  }
                  audio.play().catch(err => {
                    setTtsActive(false);
                    setTtsLoading(false);
                    setTtsPaused(false);
                    setCurrentAudio(null);
                    if (highlightCleanupRef.current) {
                      highlightCleanupRef.current();
                      highlightCleanupRef.current = null;
                    }
                    reject(err);
                  });
                };
                audio.onplay = () => {
                  setTtsLoading(false);
                  setTtsActive(true);
                  setTtsPaused(false);
                };
                audio.onpause = () => { if (ttsActive) setTtsPaused(true); };
                audio.onended = () => {
                  if (highlightCleanupRef.current) {
                    highlightCleanupRef.current();
                    highlightCleanupRef.current = null;
                  }
                  setCurrentAudio(null);
                  ws.close();
                  resolve();
                };
                audio.onerror = () => {
                  if (highlightCleanupRef.current) {
                    highlightCleanupRef.current();
                    highlightCleanupRef.current = null;
                  }
                  setTtsActive(false);
                  setTtsLoading(false);
                  setTtsPaused(false);
                  setCurrentAudio(null);
                  reject(new Error('Audio playback error'));
                };
              } else if (response.error) {
                setTtsActive(false);
                setTtsLoading(false);
                reject(new Error(response.error));
              }
            } catch (err) {
              setTtsActive(false);
              setTtsLoading(false);
              reject(err);
            }
          };
          ws.onerror = (error) => reject(error);
        });
      }
      advanceAndContinue();
    } catch (error) {
      console.error('TTS connection error:', error);
      setTtsActive(false);
      setTtsLoading(false);
      setCurrentAudio(null);
      if (currentWebSocket) {
        currentWebSocket.close();
        setCurrentWebSocket(null);
      }
      alert('Failed to connect to TTS service or play audio. Please try again.');
    }
  };

  // TTS function to read from a specific word to the end of the page
  const handleReadFromWord = async (wordId: number | string, pageIndex: number, isLeftPage: boolean) => {
    if (!isbn) return;
    
    // Get blocks for the page from WordDataContext
    const blocks = wordDataState.blocksByPage.get(pageIndex) || [];
    
    // Get all read blocks, sorted by read order
    const readBlocks = blocks
      .filter(block => block.readBlock === true)
      .sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
    
    if (readBlocks.length === 0) {
      alert('No read blocks found on this page');
      return;
    }
    
    // Find the word and determine which block it's in
    let startBlockIndex = -1;
    let startWordIndex = -1;
    let found = false;
    
    for (let blockIdx = 0; blockIdx < readBlocks.length; blockIdx++) {
      const block = readBlocks[blockIdx];
      const wordIdx = block.words?.findIndex((w: any) => {
        const wId = typeof w.wordId === 'string' ? parseInt(w.wordId, 10) : w.wordId;
        const targetId = typeof wordId === 'string' ? parseInt(wordId, 10) : wordId;
        return wId === targetId;
      });
      
      if (wordIdx !== undefined && wordIdx >= 0) {
        startBlockIndex = blockIdx;
        startWordIndex = wordIdx;
        found = true;
        break;
      }
    }
    
    if (!found) {
      alert('Word not found on this page');
      return;
    }
    
    // Build text from the word onwards
    const textParts: string[] = [];
    
    // Add remaining words from the starting block
    const startBlock = readBlocks[startBlockIndex];
    if (startBlock.words && startWordIndex < startBlock.words.length) {
      const remainingWords = startBlock.words.slice(startWordIndex);
      textParts.push(remainingWords.map((w: any) => w.text).join(' '));
    }
    
    // Add all words from subsequent blocks
    for (let blockIdx = startBlockIndex + 1; blockIdx < readBlocks.length; blockIdx++) {
      const block = readBlocks[blockIdx];
      const prevBlock = readBlocks[blockIdx - 1];
      
      // Add . if block type changes
      if (prevBlock.blockType !== block.blockType) {
        textParts.push('.');
      }
      
      if (block.words && block.words.length > 0) {
        textParts.push(block.words.map((w: any) => w.text).join(' '));
      }
    }
    
    const readOrderText = textParts.join(' ').trim();
    
    if (!readOrderText) {
      alert('No text found from this word to the end of the page');
      return;
    }
    
    // Validate voice selection for ElevenLabs
    const audioService = spreadEditorRef.current?.getAudioService() || 'azure';
    const voiceName = spreadEditorRef.current?.getSelectedVoice() || '';
    
    if (audioService === 'elevenlabs' && (!voiceName || voiceName.trim() === '' || voiceName === 'Select voice...')) {
      // Reset TTS state since play never happened (same as pressing stop)
      setTtsActive(false);
      setTtsLoading(false);
      setTtsPaused(false);
      toast.error('Please select a voice before playing', {
        duration: 3000,
      });
      return;
    }
    
    // Use the same TTS logic as continueReadingBook but with the specific text
    setTtsActive(true);
    setTtsLoading(true);
    
    try {
      // Get voice settings from SpreadEditor (re-fetch in case it changed)
      const finalVoiceName = voiceName || 'default';
      const elevenlabsModel = spreadEditorRef.current?.getElevenlabsModel() || 'default';
      
      // Determine WebSocket endpoint based on service
      const wsEndpoint = audioService === 'elevenlabs'
        ? (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'ws://localhost:3000/ws'
            : 'wss://audio.getcurioso.com/ws')
        : "wss://kuekeqxdof.execute-api.us-east-2.amazonaws.com/dev";
      
      const ws = new WebSocket(wsEndpoint);
      setCurrentWebSocket(ws);
      
      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = (error) => reject(error);
        ws.onclose = () => {};
      });
      
      const md5Id = CryptoJS.MD5(readOrderText).toString();
      
      const message: any = {
        data: readOrderText,
        area: "book",
        areaItemId: isbn,
        itemSubId: String(pageIndex - 1),
        audioService: audioService,
        voiceName: finalVoiceName,
        md5Id: md5Id
      };
      
      // Add model parameter only for ElevenLabs
      if (audioService === 'elevenlabs' && elevenlabsModel) {
        message.model = elevenlabsModel;
      }
      
      ws.send(JSON.stringify(message));
      
      ws.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          
          if (response.audioAvailable && response.audioUrl) {
            // Process word timings if available (for handleReadFromWord) - only if highlighting is enabled
            if (ENABLE_WORD_HIGHLIGHTING && response.wordTimings && Array.isArray(response.wordTimings) && response.wordTimings.length > 0) {
              // Mark words that were included (from startWordIndex to end of page)
              const blocks = wordDataState.blocksByPage.get(pageIndex) || [];
              const readBlocks = blocks
                .filter(block => block.readBlock === true)
                .sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
              
              // Find start block and word index
              let startBlockIndex = -1;
              let startWordIndex = -1;
              for (let blockIdx = 0; blockIdx < readBlocks.length; blockIdx++) {
                const block = readBlocks[blockIdx];
                const wordIdx = block.words?.findIndex((w: any) => {
                  const wId = typeof w.wordId === 'string' ? parseInt(w.wordId, 10) : w.wordId;
                  const targetId = typeof wordId === 'string' ? parseInt(wordId, 10) : wordId;
                  return wId === targetId;
                });
                if (wordIdx !== undefined && wordIdx >= 0) {
                  startBlockIndex = blockIdx;
                  startWordIndex = wordIdx;
                  break;
                }
              }
              
              if (startBlockIndex >= 0) {
                // Mark blocks and words from startWordIndex to end
                setTimeout(() => {
                  const tempBlocksByPage = new Map<number, any[]>();
                  tempBlocksByPage.set(pageIndex, blocks);
                  markTtsIncludedElements([pageIndex], tempBlocksByPage);
                  
                  // Then unmark words before startWordIndex
                  const pageContainer = document.querySelector(
                    `.word-overlay-container[data-page-overlay="page-${pageIndex}"]`
                  ) as HTMLElement;
                  
                  if (pageContainer) {
                    for (let blockIdx = 0; blockIdx < startBlockIndex; blockIdx++) {
                      const block = readBlocks[blockIdx];
                      const blockEl = pageContainer.querySelector(
                        `.overlay-block[data-block-id="${block.blockId}"]`
                      ) as HTMLElement;
                      if (blockEl) {
                        blockEl.removeAttribute('data-tts-included');
                        blockEl.querySelectorAll('.overlay-word').forEach(wordEl => {
                          wordEl.removeAttribute('data-tts-included');
                        });
                      }
                    }
                    
                    // Also unmark words before startWordIndex in the start block
                    if (startBlockIndex >= 0 && startWordIndex > 0) {
                      const startBlock = readBlocks[startBlockIndex];
                      const startBlockEl = pageContainer.querySelector(
                        `.overlay-block[data-block-id="${startBlock.blockId}"]`
                      ) as HTMLElement;
                      if (startBlockEl && startBlock.words) {
                        for (let wordIdx = 0; wordIdx < startWordIndex; wordIdx++) {
                          const word = startBlock.words[wordIdx];
                          const wordEl = startBlockEl.querySelector(
                            `.overlay-word[data-word-id="${word.wordId}"]`
                          ) as HTMLElement;
                          if (wordEl) {
                            wordEl.removeAttribute('data-tts-included');
                          }
                        }
                      }
                    }
                  }
                  
                  // Process word timings
                  const matchedCount = processWordTimings(response.wordTimings, [pageIndex]);
                  console.log(`[BookReader] Processed ${matchedCount} word timings for handleReadFromWord`);
                }, 200);
              }
            }
            
            const audio = new Audio(response.audioUrl);
            setCurrentAudio(audio);
            
            audio.onloadeddata = () => {
              setTtsLoading(false);
              
              // Start word highlighting if wordTimings are available and highlighting is enabled
              if (ENABLE_WORD_HIGHLIGHTING && response.wordTimings && Array.isArray(response.wordTimings) && response.wordTimings.length > 0) {
                // Cleanup any existing highlighting
                if (highlightCleanupRef.current) {
                  highlightCleanupRef.current();
                  highlightCleanupRef.current = null;
                }
                
                // Start highlighting
                const cleanup = startWordHighlighting(audio, response.wordTimings);
                highlightCleanupRef.current = cleanup;
              }
              
              audio.play().catch(error => {
                console.error('Audio play failed:', error);
                if (ENABLE_WORD_HIGHLIGHTING && highlightCleanupRef.current) {
                  highlightCleanupRef.current();
                  highlightCleanupRef.current = null;
                }
                setTtsActive(false);
                setTtsLoading(false);
                setTtsPaused(false);
                setCurrentAudio(null);
                alert('Failed to play audio. Please try again.');
              });
            };
            
            audio.onplay = () => {
              setTtsLoading(false);
              setTtsActive(true);
              setTtsPaused(false);
            };
            
            audio.onpause = () => {
              if (ttsActive) {
                setTtsPaused(true);
              }
            };
            
            audio.onended = () => {
              if (ENABLE_WORD_HIGHLIGHTING && highlightCleanupRef.current) {
                highlightCleanupRef.current();
                highlightCleanupRef.current = null;
              }
              setCurrentAudio(null);
              ws.close();
              setTtsActive(false);
              setTtsLoading(false);
              setTtsPaused(false);
            };
            
            audio.onerror = (error) => {
              console.error('Audio playback error:', error);
              if (ENABLE_WORD_HIGHLIGHTING && highlightCleanupRef.current) {
                highlightCleanupRef.current();
                highlightCleanupRef.current = null;
              }
              setTtsActive(false);
              setTtsLoading(false);
              setTtsPaused(false);
              setCurrentAudio(null);
              alert('Failed to play audio. Please try again.');
            };
          } else if (response.error) {
            console.error('TTS error:', response.error);
            setTtsActive(false);
            setTtsLoading(false);
            alert('TTS service error: ' + response.error);
          }
        } catch (error) {
          console.error('Error parsing TTS response:', error);
          setTtsActive(false);
          setTtsLoading(false);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setTtsActive(false);
        setTtsLoading(false);
        alert('Failed to connect to TTS service. Please try again.');
      };
      
    } catch (error) {
      console.error('TTS connection error:', error);
      setTtsActive(false);
      setTtsLoading(false);
      alert('Failed to connect to TTS service. Please try again.');
    }
  };

  // Azure TTS functionality
  const handleTTSPlay = async () => {
    setTtsActive(true);
    await continueReadingBook(currentRightPage, currentLeftPage);
  };

  const handleTTSStop = () => {
    // Cleanup word highlighting (only if enabled)
    if (ENABLE_WORD_HIGHLIGHTING) {
      if (highlightCleanupRef.current) {
        highlightCleanupRef.current();
        highlightCleanupRef.current = null;
      }
      cleanupWordHighlighting();
    }
    
    // First, remove all event listeners to prevent them from firing
    if (currentAudio) {
      currentAudio.onpause = null;
      currentAudio.onplay = null;
      currentAudio.onended = null;
      currentAudio.onerror = null;
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    
    // Close WebSocket connection if open
    if (currentWebSocket && currentWebSocket.readyState === WebSocket.OPEN) {
      currentWebSocket.close();
      setCurrentWebSocket(null);
    }
    
    // Reset TTS state - do this last to ensure everything is cleaned up
    setTtsActive(false);
    setTtsLoading(false);
    setTtsPaused(false);
  };

  const handleTTSPause = () => {
    if (currentAudio) {
      if (ttsPaused) {
        // Resume playing
        currentAudio.play().catch(error => {
          console.error('Audio resume failed:', error);
        });
        setTtsPaused(false);
      } else {
        // Pause playing
        currentAudio.pause();
        setTtsPaused(true);
      }
    }
  };

  const handleReadMe = () => {
    if (ttsLoading) {
      // Don't do anything while loading
      return;
    } else if (ttsActive && !ttsPaused) {
      // Audio is playing, pause it
      handleTTSPause();
    } else if (ttsPaused) {
      // Audio is paused, resume it
      handleTTSPause();
    } else {
      // Start new playback
      handleTTSPlay();
    }
  };

  const handleApprovalChange = async (newApproval: any) => {
    try {
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      if (!folder) {
        throw new Error('Folder is required but not set');
      }
      const { authenticatedPut } = await import('../utils/api');
      const approvalUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/approval`);
      const response = await authenticatedPut(approvalUrl, { approval: newApproval });

      if (response.ok) {
        setApproval(newApproval);
        // Update spine data to keep it in sync
        setSpineData((prev: any) => ({ ...prev, approval: newApproval }));
      } else {
        console.error('Failed to update approval:', await response.text());
      }
    } catch (error) {
      console.error('Error updating approval:', error);
    }
  };



  useEffect(() => {
    const calculateDimensions = () => {
      if (pages.length === 0) return;
      const viewer = document.querySelector('.book-viewer');
      if (!viewer) return;

      // Get first page dimensions as reference
      const refPage = pages[0];
      const pageAspectRatio = refPage.width / refPage.height; // Single page aspect ratio
      
      // Available space (accounting for left panel and top panel)
      const leftPanelWidth = 420;
      const topPanelHeight = 50;
      const availableWidth = window.innerWidth - leftPanelWidth;
      const availableHeight = window.innerHeight - topPanelHeight;
      
      // Book aspect ratio: two pages side by side + crease
      const creaseWidth = 1;
      const bookAspectRatio = (pageAspectRatio * 2) + (creaseWidth / refPage.height);
      
      // Calculate what size would fit if we prioritize width
      const maxWidthForHeight = availableHeight * bookAspectRatio;
      const widthConstrained = maxWidthForHeight <= availableWidth;
      
      // Calculate what size would fit if we prioritize height  
      const maxHeightForWidth = availableWidth / bookAspectRatio;
      const heightConstrained = maxHeightForWidth <= availableHeight;
      
      let finalWidth, finalHeight;
      
      if (widthConstrained) {
        // Height is the limiting factor - scale to fit height
        finalHeight = availableHeight;
        finalWidth = finalHeight * bookAspectRatio;
      } else {
        // Width is the limiting factor - scale to fit width
        finalWidth = availableWidth;
        finalHeight = finalWidth / bookAspectRatio;
      }
      
      // Debug logging
      console.log('[RESIZE] Available space:', { availableWidth, availableHeight });
      console.log('[RESIZE] Page aspect ratio:', pageAspectRatio);
      console.log('[RESIZE] Book aspect ratio:', bookAspectRatio);
      console.log('[RESIZE] Constraints:', { widthConstrained, heightConstrained });
      console.log('[RESIZE] Final dimensions:', { finalWidth, finalHeight });
      
      // Set container dimensions
      (viewer as HTMLElement).style.width = `${finalWidth}px`;
      (viewer as HTMLElement).style.height = `${finalHeight}px`;
      
      // Set nav column heights and positions to match book-viewer
      setTimeout(() => {
        const leftNav = document.querySelector('.nav-column.left') as HTMLElement;
        const rightNav = document.querySelector('.nav-column.right') as HTMLElement;
        if (leftNav && rightNav && finalHeight > 0) {
          const viewerRect = (viewer as HTMLElement).getBoundingClientRect();
          
          // Set height to match book height
          leftNav.style.height = `${finalHeight}px`;
          rightNav.style.height = `${finalHeight}px`;
          
          // Position to align with book-viewer
          leftNav.style.top = `${viewerRect.top}px`;
          rightNav.style.top = `${viewerRect.top}px`;
        }
      }, 0);
      
      // Trigger word overlay recalculation
      wordDataActions.recalculateOverlays();
    };

    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, [pages, wordDataActions]);

  const handlePageClick = (pageData: PageData) => {
    // Update activeSpread in WordDataContext when spread changes
    wordDataActions.setActiveSpread(
      currentLeftPage > 0 ? currentLeftPage : null,
      currentRightPage > 0 ? currentRightPage : null
    );
    
    // Load word data for the clicked page if available
    // Only load if text_extracted is "enhanced" for this page
    let wordsFilename: string | null = null;
    if (pageData.text_extracted === 'enhanced' || pageData.text_extracted === true) {
      if (pageData.words) {
        wordsFilename = pageData.words;
      } else if (pageData['words-enhanced']) {
        wordsFilename = pageData['words-enhanced'];
      }
    }
    
    if (wordsFilename && isbn && pageData['page-index']) {
      wordDataActions.loadPageBlocks(pageData['page-index'], isbn, wordsFilename);
    }
  };

  const handleWordClick = (word: any, block: any) => {
    wordDataActions.setSelectedWord(String(word.wordId));
    // Selection is now handled in PageEditor with pageIndex
  };


  const updatePositionClasses = () => {
    // Only render current page and adjacent pages (lazy loading)
    const visiblePageIndices = new Set<number>();
    
    // Add current pages
    visiblePageIndices.add(currentRightPage);
    if (currentLeftPage > 0) { // Only add left page if it exists (not 0)
      visiblePageIndices.add(currentLeftPage);
    }
    
    // Add adjacent pages (next and previous)
    if (currentRightPage + 2 <= pages.length) visiblePageIndices.add(currentRightPage + 2);
    if (currentRightPage - 2 >= 1) visiblePageIndices.add(currentRightPage - 2);
    if (currentLeftPage > 0) {
      if (currentLeftPage + 2 <= pages.length) visiblePageIndices.add(currentLeftPage + 2);
      if (currentLeftPage - 2 >= 1) visiblePageIndices.add(currentLeftPage - 2);
    }
    
    console.log(`[BookReader] updatePositionClasses: visiblePageIndices=`, Array.from(visiblePageIndices).sort(), `currentSpread=(${currentLeftPage}, ${currentRightPage})`);
    
    // Only render visible pages
    const elements = pages
      .filter(page => visiblePageIndices.has(page['page-index']))
      .map(page => {
        let positionClass = '';
        const pageIndex = page['page-index'];
        const isRightPage = (pageIndex % 2 === 1);
        
        if (isRightPage) {
          if (pageIndex === currentRightPage) positionClass = 'current-right-page';
          else if (pageIndex === currentRightPage + 2) positionClass = 'next-right-page';
          else if (pageIndex === currentRightPage - 2) positionClass = 'previous-right-page';
          else positionClass = 'future-right-page';
        } else {
          if (pageIndex === currentLeftPage) positionClass = 'current-left-page';
          else if (pageIndex === currentLeftPage + 2) positionClass = 'next-left-page';
          else if (pageIndex === currentLeftPage - 2) positionClass = 'previous-left-page';
          else positionClass = 'future-left-page';
        }
        
        return (
          <Page 
            key={`page-${page['page-index']}-spread-${currentLeftPage}-${currentRightPage}`} 
            pageIndex={page['page-index']} 
            filename={page.filename} 
            isbn={isbn || ''} 
            imageDimensions={{width: page.width, height: page.height}} 
            showWords={wordDataState.showWords} 
            positionClass={positionClass}
            pageData={page}
            onPageClick={handlePageClick}
            onWordClick={handleWordClick}
            currentLeftPage={currentLeftPage}
            currentRightPage={currentRightPage}
            showOverlays={showOverlays}
            onPageTypeChange={handlePageTypeChange}
          />
        );
      });
    setAllPageElements(elements);
  };

  const nextPage = () => {
    // If already on the last page, don't allow further navigation
    if (isAnimating || currentLeftPage === totalPages) return;
    
    // Allow navigation if there's a full spread ahead, OR if the next page is the last page (left page only)
    const canNavigateFullSpread = currentRightPage !== null && currentRightPage + 2 <= totalPages;
    const canNavigateToLastPage = currentRightPage !== null && currentRightPage + 1 === totalPages;
    
    if (!canNavigateFullSpread && !canNavigateToLastPage) return;
    
    // Stop TTS if active when user navigates manually
    if (ttsActive) {
      handleTTSStop();
    }
    
    setIsAnimating(true);
    wordDataActions.clearSelection();
    
    let newRightPage: number | null;
    let newLeftPage: number | null;
    
    if (canNavigateFullSpread) {
      // Normal case: full spread ahead
      newRightPage = currentRightPage! + 2;
      newLeftPage = currentLeftPage === 0 ? 2 : currentLeftPage + 2;
    } else {
      // Last page case: only left page exists
      newRightPage = null;
      newLeftPage = totalPages;
    }
    
    setCurrentRightPage(newRightPage);
    setCurrentLeftPage(newLeftPage);
    
    // Update URL asynchronously to avoid blocking
    setTimeout(() => {
      updatePageInUrl(newRightPage ?? totalPages);
    }, 0);
    
    // Use CSS transition duration
    setTimeout(() => setIsAnimating(false), 150);
  };

  const previousPage = () => {
    if (isAnimating) return;
    
    // If we're on the last page (left page only), go back to the previous spread
    if (currentLeftPage === totalPages && currentRightPage === null) {
      // Go back to the spread before the last page
      const prevRightPage = totalPages - 1;
      const prevLeftPage = totalPages - 2;
      
      // Stop TTS if active when user navigates manually
      if (ttsActive) {
        handleTTSStop();
      }
      
      setIsAnimating(true);
      wordDataActions.clearSelection();
      
      setCurrentRightPage(prevRightPage);
      setCurrentLeftPage(prevLeftPage);
      
      // Update URL asynchronously to avoid blocking
      setTimeout(() => {
        updatePageInUrl(prevRightPage);
      }, 0);
      
      // Use CSS transition duration
      setTimeout(() => setIsAnimating(false), 150);
      return;
    }
    
    // Normal case: check if we can go back
    if (currentRightPage === null || currentRightPage <= 1) return;
    
    // Stop TTS if active when user navigates manually
    if (ttsActive) {
      handleTTSStop();
    }
    
    setIsAnimating(true);
    wordDataActions.clearSelection();
    
    const newRightPage = currentRightPage - 2;
    const newLeftPage = currentLeftPage <= 2 ? 0 : currentLeftPage - 2;
    
    setCurrentRightPage(newRightPage);
    setCurrentLeftPage(newLeftPage);
    
    // Update URL asynchronously to avoid blocking
    setTimeout(() => {
      updatePageInUrl(newRightPage);
    }, 0);
    
    // Use CSS transition duration
    setTimeout(() => setIsAnimating(false), 150);
  };



  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Temporary flag to disable fn+r and fn+m shortcuts
      const ENABLE_RESIZE_MOVE_SHORTCUTS = false;
      
      // Handle resize (r) and move (m) shortcuts - these work even in edit mode
      // Only trigger if no modifier keys are pressed (fn key doesn't register as a modifier)
      if (ENABLE_RESIZE_MOVE_SHORTCUTS) {
        const hasModifier = e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;
        
        if ((e.key === 'r' || e.key === 'R') && !hasModifier) {
          e.preventDefault();
          e.stopPropagation();
          // Call the same function as the resize button
          spreadEditorRef.current?.setEditMode(wordDataState.editMode === 'resize' ? null : 'resize');
          return;
        } else if ((e.key === 'm' || e.key === 'M') && !hasModifier) {
          e.preventDefault();
          e.stopPropagation();
          // Call the same function as the move button
          spreadEditorRef.current?.setEditMode(wordDataState.editMode === 'move' ? null : 'move');
          return;
        }
      }
      
      // Handle arrow keys for page navigation
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        // Don't handle arrow keys if in edit mode (let ESC handle exiting edit mode)
        if (wordDataState.editMode !== null) {
          return; // Prevent page navigation when in edit mode
        }
        
        // Don't handle arrow keys if user is actively editing text in an input field
        // (but allow if they're just focused, not typing)
        const target = e.target as HTMLElement;
        const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        const isContentEditable = target.isContentEditable && 
                                   target.getAttribute('contenteditable') === 'true' &&
                                   // Allow arrow keys in word overlays (they're not really editable text fields)
                                   !target.closest('.word-overlay');
        
        if (isInputElement || isContentEditable) {
          return; // Let the input handle its own keyboard events
        }
        
        // Always prevent default for arrow keys to stop browser scrolling
        e.preventDefault();
        e.stopPropagation();
        
        if (e.key === 'ArrowRight') {
          nextPage();
        } else if (e.key === 'ArrowLeft') {
          previousPage();
        }
        return;
      }
      
      // Handle Escape key
      if (e.key === 'Escape') {
        const target = e.target as HTMLElement;
        const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        
        // Check if any block is in edit mode - if so, close all block editors
        if (spreadEditorRef.current?.isBlockEditing()) {
          e.preventDefault();
          e.stopPropagation();
          spreadEditorRef.current.closeAllBlockEditors();
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        // Check if merge is in progress - if so, cancel it and return early
        if (spreadEditorRef.current?.cancelMerge()) {
          return; // Merge was active and canceled, don't process other Escape handlers
        }
        
        // Check if we're in an edit mode - if so, turn it off
        if (wordDataState.editMode !== null) {
          // Turn off edit mode
          spreadEditorRef.current?.setEditMode(null);
          return;
        }
        
        // Default action: require double-press within 1 second to close book
        const now = Date.now();
        const lastPress = lastEscapePressRef.current;
        
        if (lastPress !== null && (now - lastPress) < 1000) {
          // Second press within 1 second - close the book
          lastEscapePressRef.current = null; // Reset
          handleClose();
          return;
        } else {
          // First press or more than 1 second since last press - do nothing but record time
          lastEscapePressRef.current = now;
          return;
        }
      }
    };

    // Use capture phase to ensure we catch the event early
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isAnimating, currentRightPage, totalPages, handleClose, wordDataState.editMode, nextPage, previousPage, spreadEditorRef]);

  // Show message if no book data is available
  if (pages.length === 0) {
    return (
      <div className="book-reader-container" style={{ display: 'block' }}>
        <button className="close-button" onClick={handleClose}>×</button>
        
        {/* Top Panel */}
        <div className="top-panel">
          {/* Top panel content goes here */}
        </div>
        
        {/* Left Panel */}
        <div className="left-panel">
          {/* Left panel content goes here */}
        </div>
        
        <div className="book-container">
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            textAlign: 'center',
            padding: '2rem'
          }}>
            <h2>Book Reader - Limited Mode</h2>
            
            {bookMetadata ? (
              <>
                <div style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  padding: '1.5rem', 
                  borderRadius: '0.5rem', 
                  marginBottom: '2rem',
                  border: '1px solid var(--border-primary)',
                  maxWidth: '500px'
                }}>
                  <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                    {bookMetadata.book_title}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                    <div><strong>ISBN:</strong> {bookMetadata.isbn}</div>
                    <div><strong>Pages:</strong> {bookMetadata.page_count}</div>
                    <div><strong>AI Confidence:</strong> {bookMetadata['ai-confidence']}%</div>
                    <div><strong>Fast Track:</strong> {bookMetadata.fast_track ? 'Yes' : 'No'}</div>
                    <div><strong>Images Created:</strong> {bookMetadata.images_created ? 'Yes' : 'No'}</div>
                    <div><strong>Text Extracted:</strong> {bookMetadata.text_extracted}</div>
                  </div>
                  {bookMetadata.possible_issues && (
                    <div style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
                      <strong>Issues:</strong> {bookMetadata.possible_issues}
                    </div>
                  )}
                </div>
                <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  This book is loaded from your uploaded book list. Individual page images and text extraction data are not yet available for viewing.
                </p>
              </>
            ) : (
              <>
                <p style={{ marginBottom: '1rem' }}>
                  This book was not found in your uploaded book list.
                </p>
                <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  The book reader requires individual page images and text extraction data to function properly.
                </p>
                <p style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                  Book: {isbn}
                </p>
              </>
            )}
            
            <button 
              onClick={handleClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--accent-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Back to Book List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="book-reader-container" style={{ display: 'block' }}>
      <button className="close-button" onClick={handleClose}>×</button>
      
      {/* Top Panel */}
      <div className="top-panel">
        {isbn && spreadEditorRef.current && (
          <TopEditor
            approval={approval}
            currentApproval={topEditorState.currentApproval}
            onApprovalChange={handleApprovalChange}
            onNotesChange={(notes) => spreadEditorRef.current?.handleNotesChange(notes)}
            onSave={() => spreadEditorRef.current?.handleSave()}
            hasChanges={topEditorState.hasChanges}
            isSaving={topEditorState.isSaving}
            saveButtonText={topEditorState.saveButtonText}
            saveButtonTitle={topEditorState.saveButtonTitle}
            onAddBlockLeft={() => {
              const leftPageIndex = spreadEditorRef.current?.getLeftPageIndex();
              if (leftPageIndex !== null && leftPageIndex !== undefined) {
                spreadEditorRef.current?.handleAddBlock(leftPageIndex, true);
              }
            }}
            onAddBlockRight={() => {
              const rightPageIndex = spreadEditorRef.current?.getRightPageIndex();
              if (rightPageIndex !== null && rightPageIndex !== undefined) {
                spreadEditorRef.current?.handleAddBlock(rightPageIndex, false);
              }
            }}
          />
        )}
      </div>
      
      {/* Left Panel */}
      <div className="left-panel">
        {/* SpreadEditor Component */}
        {isbn && (
          <SpreadEditor 
            ref={spreadEditorRef}
            isbn={isbn || ''} 
            approval={approval} 
            onApprovalChange={handleApprovalChange}
            onReadMe={handleReadMe}
            onStop={handleTTSStop}
            onReadFromWord={handleReadFromWord}
            ttsActive={ttsActive}
            ttsLoading={ttsLoading}
            ttsPaused={ttsPaused}
            currentLeftPage={currentLeftPage}
            currentRightPage={currentRightPage}
            pages={pages}
            onShowOverlaysChange={setShowOverlays}
            onPageTypeChange={handlePageTypeChange}
            onStateChange={setTopEditorState}
          />
        )}
      </div>
      
      <div className="book-container">
        <div 
          className={`nav-column left ${wordDataState.editMode !== null || isBlockEditing ? 'hidden' : ''}`} 
          onClick={() => !isAnimating && previousPage()}
        >
          <div className="nav-arrow">◀</div>
        </div>
        <div 
          className={`nav-column right ${wordDataState.editMode !== null || isBlockEditing ? 'hidden' : ''}`} 
          onClick={() => !isAnimating && nextPage()}
        >
          <div className="nav-arrow">▶</div>
        </div>
        
        <div className="book-viewer">
          <div ref={pageContainerRef} className="page-container" tabIndex={-1}>
            <div className="center-crease" />
            {allPageElements}
            
          </div>
        </div>
      </div>

    </div>
  );
};

export default BookReader;
